/* ==========================================================================
   FLC Forge — Cart Drawer
   ========================================================================== */

const CART_SECTION = 'cart-line-items';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer      = this.querySelector('[data-cart-drawer-inner]');
    this.overlay     = document.getElementById('overlay');
    this.closeBtn    = this.querySelector('[data-cart-close]');
    this.itemsWrap   = this.querySelector('[data-cart-items]');
    this.emptyState  = this.querySelector('[data-cart-empty]');
    this.footer      = this.querySelector('[data-cart-footer]');
    this.shippingWraps = this.querySelectorAll('[data-cart-shipping-wrap]');
    this.subtotal    = this.querySelectorAll('[data-cart-subtotal]');
    this.noteWrap    = this.querySelector('[data-cart-note-wrap]');
    this.noteInput   = this.noteWrap?.querySelector('textarea[name="note"]');
    this.progressBar = this.querySelector('[data-cart-progress]');
    this.noteSaveTimeout = null;
    this.noteSaveRequest = null;

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('flc:cart:open',       () => this.open());
    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // flc:cart:updated may carry pre-rendered section HTML (from add-to-cart flows)
    document.addEventListener('flc:cart:updated', (e) => this.renderCart(e.detail));
    document.addEventListener('flc:cart:refresh',  () => this.refreshCart());

    this.itemsWrap?.addEventListener('change', (e) => {
      if (e.target.matches('[data-quantity-input]')) this.updateQuantity(e.target);
    });

    this.itemsWrap?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove-item]');
      if (btn) this.removeItem(btn.dataset.key);
    });

    this.noteInput?.addEventListener('input', () => this.scheduleNoteSave());

    this.querySelector('[data-checkout-btn]')?.addEventListener('click', async () => {
      await this.flushNoteSave();
      window.location.href = window.flcForge.routes.checkout_url;
    });
  }

  /* ---- Open / close ---- */

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.drawer?.classList.add('is-open');
    FLC.openOverlay('cart-drawer');
    FLC.trapFocus(this);
    this.refreshCart();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-open');
    FLC.closeOverlay('cart-drawer');
  }

  setLoading(state) {
    this.progressBar?.classList.toggle('is-loading', state);
  }

  scheduleNoteSave() {
    if (!this.noteInput) return;
    clearTimeout(this.noteSaveTimeout);
    this.noteSaveTimeout = setTimeout(() => {
      this.noteSaveRequest = this.saveNote(this.noteInput.value);
    }, 350);
  }

  async flushNoteSave() {
    if (!this.noteInput) return;
    clearTimeout(this.noteSaveTimeout);
    this.noteSaveRequest = this.saveNote(this.noteInput.value);
    await this.noteSaveRequest;
  }

  async saveNote(note) {
    this.setLoading(true);
    try {
      await FLC.fetchJSON(window.flcForge.routes.cart_update_url, {
        method: 'POST',
        body: JSON.stringify({ note })
      });
    } catch (err) {
      console.error('Cart note update failed:', err);
    } finally {
      this.setLoading(false);
      this.noteSaveRequest = null;
    }
  }

  /* ---- Fetch helpers ---- */

  // Full refresh: cart meta + rendered items in parallel
  async refreshCart() {
    this.setLoading(true);
    try {
      const [cart, sections] = await Promise.all([
        FLC.fetchJSON(`${window.flcForge.routes.cart_url}.js`),
        FLC.fetchJSON(`/?sections=${CART_SECTION}`)
      ]);
      this.renderCart(cart, sections[CART_SECTION]);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  // Fetch only the rendered items HTML (fallback when sections weren't in the cart response)
  async fetchItemsHTML() {
    const data = await FLC.fetchJSON(`/?sections=${CART_SECTION}`);
    return data[CART_SECTION] ?? '';
  }

  /* ---- Render ---- */

  async renderCart(cart, itemsHTML) {
    const isEmpty = cart.item_count === 0;

    this.updateCount(cart.item_count);
    this.subtotal.forEach(el => { el.textContent = FLC.formatMoney(cart.total_price); });
    this.updateShippingBar(cart.total_price);
    if (this.noteInput && document.activeElement !== this.noteInput) {
      this.noteInput.value = cart.note || '';
    }

    // Use pre-rendered HTML if provided; otherwise fetch it
    if (this.itemsWrap) {
      this.itemsWrap.innerHTML = itemsHTML ?? await this.fetchItemsHTML();
      FLC.loadCompleteLook(this.itemsWrap);
      // Restart the fade-in on every refresh (add, remove, quantity change)
      // instead of the list just popping into place.
      this.itemsWrap.classList.remove('is-fading-in');
      void this.itemsWrap.offsetWidth; // force reflow so the animation replays
      this.itemsWrap.classList.add('is-fading-in');
    }

    if (this.itemsWrap)    this.itemsWrap.hidden    = isEmpty;
    if (this.emptyState)   this.emptyState.hidden   = !isEmpty;
    if (this.footer)       this.footer.hidden       = isEmpty;
    this.shippingWraps.forEach(el => { el.hidden = isEmpty; });
  }

  updateShippingBar(totalPrice) {
    this.querySelectorAll('[data-shipping-bar]').forEach(bar => {
      const threshold = parseInt(bar.dataset.threshold, 10);
      if (!threshold) return;

      const track   = bar.querySelector('[data-shipping-bar-track]');
      const fill    = bar.querySelector('[data-shipping-bar-fill]');
      const message = bar.querySelector('[data-shipping-bar-message]');
      const percent = Math.min(100, Math.round((totalPrice / threshold) * 100));

      if (track) track.setAttribute('aria-valuenow', percent);
      if (fill) fill.style.width = `${percent}%`;

      if (message) {
        const remaining = threshold - totalPrice;
        if (remaining <= 0) {
          message.textContent = bar.dataset.reachedText || '';
        } else {
          const template = bar.dataset.progressText || '';
          message.textContent = template.replace('{{ amount }}', FLC.formatMoney(remaining));
        }
      }
    });
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ---- Cart mutations ---- */

  async updateQuantity(input) {
    const key = input.dataset.key;
    const qty = parseInt(input.value, 10);
    this.setLoading(true);
    try {
      const data = await FLC.fetchJSON(window.flcForge.routes.cart_change_url, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty, sections: CART_SECTION })
      });
      this.renderCart(data, data.sections?.[CART_SECTION]);
    } catch (err) {
      console.error('Update quantity failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  async removeItem(key) {
    // Collapse the specific row while the request is in flight, so the item
    // visibly shrinks away instead of just vanishing when the list re-renders.
    const el = this.itemsWrap?.querySelector(`[data-cart-item][data-key="${key}"]`);
    const animateOut = new Promise((resolve) => {
      if (!el) { resolve(); return; }
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      el.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400); // fallback in case transitionend doesn't fire
      el.classList.add('is-removing');
    });

    this.setLoading(true);
    try {
      const [data] = await Promise.all([
        FLC.fetchJSON(window.flcForge.routes.cart_change_url, {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: 0, sections: CART_SECTION })
        }),
        animateOut
      ]);
      this.renderCart(data, data.sections?.[CART_SECTION]);
      window.liveRegion?.announce('Item removed from cart');
    } catch (err) {
      console.error('Remove item failed:', err);
    } finally {
      this.setLoading(false);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/* Cart quantity stepper buttons (outside the drawer, e.g. product page)
   ========================================================================== */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quantity-change]');
  if (!btn) return;

  const change = parseInt(btn.dataset.quantityChange, 10);
  const key    = btn.dataset.key;
  if (!key) return;

  const input = btn.parentElement?.querySelector('[data-quantity-input]');
  if (!input) return;

  const newQty = Math.max(0, parseInt(input.value, 10) + change);
  input.value = newQty;

  try {
    const data = await FLC.fetchJSON(window.flcForge.routes.cart_change_url, {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: newQty, sections: CART_SECTION })
    });
    FLC.dispatch('flc:cart:updated', data);
  } catch (err) {
    console.error('Cart change failed:', err);
  }
});
